<script setup>
import { computed, ref } from 'vue'
import { usePlaybackStore } from '@state/stores/playback'
import formatDate from '@utils/format-date-full'
import GenrePills from '@components/genre/genre-pills'
import MixOptions from '@components/mix/mix-options'

const props = defineProps({
    index: {
        type: Number,
        default: -1,
    },
    mix: {
        type: Object,
        default: () => {},
    },
    buttons: {
        type: Object,
        default: () => {},
    },
    isIndexedList: {
        type: Boolean,
        default: false,
    },
    genres: {
        type: Boolean,
        default: false,
    },
    isPlaylistMix: {
        type: Boolean,
        default: false,
    },
})

defineEmits(['play-playlist-mix'])

const playbackStore = usePlaybackStore()

const isTileHovered = ref(false)

const airDate = computed(() => (props.mix.episode ? formatDate(new Date(props.mix.episode.air_start)) : ''))

const isMixLoaded = computed(
    () => playbackStore.resource && props.mix.id === playbackStore.resource.id
)

const playbackStatus = computed(
    () => playbackStore.status === 'playing' || playbackStore.status === 'loading'
)

const tileIndex = computed(() => `${props.index + 1}`.padStart(2, '0'))
</script>

<template>
    <div
        class="playlists-table-tile inner left"
        :class="{ active: isMixLoaded && playbackStatus }"
        @mouseover="isTileHovered = true"
        @mouseleave="isTileHovered = false"
    >
        <div
            v-if="isIndexedList"
            class="index"
        >
            <div class="index-main">
                {{ tileIndex }}
            </div>
        </div>
        <div class="container-inner inner space">
            <div class="inner left">
                <div
                    class="image"
                    @click="$emit('play-playlist-mix')"
                >
                    <BaseResourceImage
                        :resource="mix.episode"
                        :playable-mix="mix"
                        :has-hover-state="isTileHovered"
                        is-playlist-mix
                        radius="card"
                    />
                </div>
                <div>
                    <div class="title">
                        <BaseLink
                            v-if="mix.artist"
                            class="title-link"
                            :to="{ path: `/artist/${mix.artist.url}/${mix.artist.id}/` }"
                        >
                            {{ mix.artist.title }}
                        </BaseLink>
                        <BasePlaceholder
                            v-else
                            :text="mix.artist && mix.artist.title"
                            type="artist"
                        />
                    </div>
                    <div class="inner left">
                        <div class="artist">
                            <div>
                                <BaseLink
                                    v-if="mix.show && mix.show.title"
                                    :to="{ name: 'show', params: { id: mix.show.id } }"
                                >
                                    {{ mix.show.title }}
                                </BaseLink>
                                <BasePlaceholder
                                    v-else
                                    :text="mix.show && mix.show.title"
                                    type="show"
                                />
                            </div>
                        </div>
                        <div class="time">
                            <div>
                                <BaseLink
                                    v-if="mix.title"
                                    :to="{ name: 'mix', params: { id: mix.id } }"
                                >
                                    {{ airDate }}
                                </BaseLink>
                                <BasePlaceholder
                                    v-else
                                    :text="mix.title"
                                    :size="10"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="inner right">
                <div
                    v-if="genres"
                    class="genre-list"
                >
                    <GenrePills :genres="mix.genre.slice(0, 2)" />
                    <BaseButton
                        v-if="mix.genre.length > 2"
                        button-type="tagSecondary"
                        size="small"
                    >
                        +{{ mix.genre.length - 2 }} MORE
                    </BaseButton>
                </div>
                <div class="buttons">
                    <BaseButton
                        v-if="mix && buttons && buttons.playCount"
                        class="button button-play-count"
                        icon="play_arrow"
                        size="small"
                        button-type="tag"
                    >
                        {{ Number(mix.reach).toLocaleString() || '0' }}
                    </BaseButton>
                    <BaseDropdown class="button">
                        <template #trigger>
                            <BaseButton
                                :modifiers="{ autoWidth: true, transparent: true, dots: true }"
                                button-type="tag"
                                aria-label="Options"
                                size="small"
                            />
                        </template>
                        <template #dropdown>
                            <div>
                                <MixOptions :mix="mix" />
                            </div>
                        </template>
                    </BaseDropdown>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@import '@design';

.playlists-table-tile {
    @extend %contentSubtitleM;

    display: flex;
    align-items: center;

    &:hover .container-inner {
        background: $c-hover-bg;
    }

    &.active .container-inner {
        background: $c-hover-bg;
    }

    &:active .container-inner {
        background: $c-active-bg;
    }
}

.container-inner {
    flex-basis: 100%;
    justify-content: space-between;
    padding: $s-s $s-m;
}

.inner {
    display: flex;
    align-items: center;

    &.left {
        justify-content: flex-start;
    }

    &.right {
        justify-content: flex-end;
    }

    &.space {
        justify-content: space-between;
    }
}

.index {
    margin-right: $s-s;
}

.index-main {
    font-size: 16px;
    color: $c-text-faded;
}

.image {
    justify-content: flex-start;
    width: 4rem;
    height: 4rem;
    margin-right: gutter(of 12 narrow);
}

.title {
    margin-bottom: $s-xxs;
    font-weight: bold;
    color: $c-white;
}

.title-link {
    &,
    &:visited {
        color: $c-white;
    }
}

.genre-list {
    display: none;

    @include desktop {
        display: flex;
        overflow: visible;
    }
}

.artist {
    margin-right: $s-m;
}

.time {
    position: relative;
    display: none;
    margin-left: $s-m;

    &::before {
        position: absolute;
        top: 0;
        left: -$s-l;
        font-size: 5px;
        content: '\25CF';
    }

    @include tablet {
        display: inherit;
    }
}

.buttons {
    display: flex;
    margin-left: auto;

    @include desktop {
        margin-left: gutter(of 12 narrow);
    }
}

.button {
    margin-left: $s-s;

    @include desktop {
        margin-left: 0;
    }
}

.button + .button {
    margin-left: $s-s;
}

.button.button-play-count {
    display: none;

    @include desktop {
        display: block;
    }
}
</style>
